import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { Typography, Button } from '@mui/material';
import CropsColumn from './crops.column';
import { useNavigate } from 'react-router-dom';
import GenericDataGrid from '../../components/genericTable';
import { Icon } from "@iconify/react";
import { getAllCrops } from './crops.service';
import { createManagementTableSx } from '../../styles/tableStyles';
import { managementCreateButtonSx, managementHeaderActionSx, managementHeaderContentSx, managementHeaderSx, managementPageSubtitleSx, managementPageTitleSx } from '../../styles/managementStyles';
import AlertToast from '../../components/alertToast';
import { buttonContentSx } from '../../styles/dialogStyles';
import BulkLinkModal from './bulkLinkModal';
import InlineAlert from '../../components/inlineAlert';

export default function Crops() {

  const navigate = useNavigate();
  const [cropsList, setCropsList] = useState([]);
  const [selectedCropIds, setSelectedCropIds] = useState([]);
  const [bulkLinkModalOpen, setBulkLinkModalOpen] = useState(false);

  const [errors, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [totalDataCount, setTotalDataCount] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [alert, setAlert] = useState('');
  const [openAlert, setOpenAlert] = useState(false);
  const [responseMsg, setResponseMsg] = useState('');

  const alertClose = () => {
    setAlert('');
    setOpenAlert(false);
  };

  const handleRowSelection = (newSelectionModel) => {
    console.log('Selection changed:', newSelectionModel);
    // Handle new MUI DataGrid v7 selection model format
    if (newSelectionModel && newSelectionModel.ids) {
      const idsArray = Array.from(newSelectionModel.ids);
      console.log('Extracted IDs:', idsArray);
      setSelectedCropIds(idsArray);
    } else if (Array.isArray(newSelectionModel)) {
      setSelectedCropIds(newSelectionModel);
    } else {
      setSelectedCropIds([]);
    }
  };

  const handleBulkLinkSuccess = (message, severity = 'success') => {
    setAlert(severity === 'success' ? 'true' : 'false');
    setResponseMsg(message);
    setOpenAlert(true);
    setSelectedCropIds([]);
    setBulkLinkModalOpen(false);
    getCropsData(currentPage, rowsPerPage);
  };

  const getCropsData = useCallback(async (page = currentPage, size = rowsPerPage) => {
    setLoading(true);
    const res = await getAllCrops(page, size);
    setLoading(false);

    if (res && res.data) {
      setCropsList(res.data.content || res.data || []);
      setTotalDataCount(res.data.totalElements || 0);
    } else {
      setCropsList([]);
      setTotalDataCount(0);
      setError("An error occurred while fetching data. Please try again later.");
    }
  }, [currentPage, rowsPerPage]);

  useEffect(() => {
    getCropsData(currentPage, rowsPerPage);
  }, [currentPage, rowsPerPage, getCropsData]);

  let cropsData = {
    'limit': '',
    'page': '',
    'navigate': navigate,
    'getCropsData': getCropsData,
    'setAlert': setAlert,
    'setOpenAlert': setOpenAlert,
    'setResponseMsg': setResponseMsg,
    'setLoading': setLoading,
  }
  const cropsColumn = CropsColumn(cropsData);
  const cropsGridSx = createManagementTableSx();

  return (
    <>

      <Box sx={managementHeaderSx}>
        <Box sx={managementHeaderContentSx}>
          <Typography className="app-page-title" sx={managementPageTitleSx}>
            Crops
          </Typography>
          <Typography sx={managementPageSubtitleSx}>
            Manage your crops database.
          </Typography>
        </Box>
        <Box sx={managementHeaderActionSx}>
          <Button
            variant="contained"
            type="submit"
            onClick={() => { setLoading(true); navigate('/addCrops') }}
            sx={{ ...managementCreateButtonSx, width: { xs: '100%', md: 'auto' }, minWidth: { xs: '100%', md: 204 } }}
          >
            <Box component="span" sx={buttonContentSx}>
              <Icon icon="tabler:plus" width="18" height="18" />
              <span>ADD CROP</span>
            </Box>
          </Button>
        </Box>
      </Box>

      {selectedCropIds.length > 0 && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          backgroundColor: '#E8EAFD',
          borderRadius: '8px',
          marginBottom: '20px',
          gap: 2,
          border: '2px solid #6366F1',
        }}>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#1C1B1F', fontSize: '16px' }}>
              {selectedCropIds.length} crop{selectedCropIds.length !== 1 ? 's' : ''} selected
            </Typography>
            <Typography variant="caption" sx={{ color: '#625B71', fontSize: '12px' }}>
              {cropsList.filter(crop => selectedCropIds.includes(crop.id)).map(crop => crop.name).join(', ')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                console.log('Opening bulk link modal with crops:', selectedCropIds);
                setBulkLinkModalOpen(true);
              }}
              startIcon={<Icon icon="tabler:link" width="18" height="18" />}
              sx={{
                backgroundColor: '#6366F1',
                '&:hover': {
                  backgroundColor: '#4F46E5',
                }
              }}
            >
              Link to Integrators
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setSelectedCropIds([])}
              sx={{
                borderColor: '#6366F1',
                color: '#6366F1',
                '&:hover': {
                  backgroundColor: '#F3F4F6',
                }
              }}
            >
              Clear
            </Button>
          </Box>
        </Box>
      )}

      <InlineAlert
        message={openAlert && alert === 'false' ? responseMsg : ''}
        onClose={alertClose}
        variant="error"
      />

      <Box sx={{ width: '100%', padding: '0 0px' }}>
        <GenericDataGrid rows={cropsList} columns={cropsColumn} rowCount={totalDataCount} page={currentPage} pageSize={rowsPerPage} loading={loading} errors={errors} checkbox='true'
          dataGridSx={cropsGridSx}
          tableMinWidth={1080}
          rowHeight={60}
          onSelectionChange={handleRowSelection}
          onPaginationChange={(page, pageSize) => {
            setCurrentPage(page);
            setRowsPerPage(pageSize);
            getCropsData(page, pageSize);
          }}
        />
      </Box>

      <AlertToast
        open={openAlert && alert === 'true'}
        onClose={alertClose}
        variant="success"
        message={responseMsg}
      />

      <BulkLinkModal
        open={bulkLinkModalOpen}
        selectedCropIds={selectedCropIds}
        selectedCrops={cropsList.filter(crop => selectedCropIds.includes(crop.id))}
        onClose={() => setBulkLinkModalOpen(false)}
        onSuccess={handleBulkLinkSuccess}
      />
    </>
  );
}
